import { formatPriceCents } from "../../lib/money";
import styles from "./RegisterSummary.module.css";
import type { RegisterTotals } from "./lib/registerFilters";

export interface RegisterSummaryProps {
	/** null while the register is still loading — the tiles hold their space. */
	totals: RegisterTotals | null;
	/** Whether the table below is currently narrowed; the caption says so. */
	filtered: boolean;
}

function plural(count: number, word: string): string {
	return `${count} ${word}${count === 1 ? "" : "s"}`;
}

/**
 * The money the register is actually about: what is owed, what came in, what
 * has not gone out yet, and what has been billed all told.
 *
 * The figures are computed over every invoice, **not** over the filtered table.
 * A summary that moves while you type is a statement about your search, not
 * about the business — so the caption states the scope permanently rather than
 * only when it might surprise someone.
 *
 * The values are plain text on purpose: as a live region they would announce
 * four figures every time the table refreshed after a payment.
 */
export function RegisterSummary({ totals, filtered }: RegisterSummaryProps) {
	const money = (cents: number): string => (totals ? formatPriceCents(cents) : "—");

	return (
		<section className={styles.root} aria-label="Invoice totals">
			<div className={styles.tiles}>
				<div className={`${styles.tile} ${styles.tileLead}`}>
					<span className={styles.tileLabel}>Outstanding</span>
					<span className={`${styles.tileValue} ${styles.tileValueLead}`}>
						{money(totals?.outstandingCents ?? 0)}
					</span>
					<span className={styles.tileMeta}>
						{totals
							? `${plural(totals.awaitingCount, "invoice")} awaiting payment${
									totals.partPaidCount > 0 ? ` · ${totals.partPaidCount} part paid` : ""
								}`
							: " "}
					</span>
				</div>

				<div className={styles.tile}>
					<span className={styles.tileLabel}>Received</span>
					<span className={styles.tileValue}>{money(totals?.receivedCents ?? 0)}</span>
					<span className={styles.tileMeta}>
						{totals ? `${plural(totals.settledCount, "invoice")} settled` : " "}
					</span>
				</div>

				<div className={styles.tile}>
					<span className={styles.tileLabel}>Drafts</span>
					<span className={styles.tileValue}>{money(totals?.draftCents ?? 0)}</span>
					<span className={styles.tileMeta}>
						{totals ? `${plural(totals.draftCount, "invoice")} not yet sent` : " "}
					</span>
				</div>

				<div className={styles.tile}>
					<span className={styles.tileLabel}>Invoiced</span>
					<span className={styles.tileValue}>{money(totals?.invoicedCents ?? 0)}</span>
					<span className={styles.tileMeta}>
						{totals ? `${plural(totals.issuedCount, "invoice")} issued` : " "}
					</span>
				</div>
			</div>
			<p className={styles.caption}>
				Across all invoices{filtered ? " — the table below is filtered." : "."}
			</p>
		</section>
	);
}
